/**
 * Frontend Environment Configuration
 * 
 * This module centralizes all environment-specific configuration.
 * All components should import from here instead of directly accessing import.meta.env
 * 
 * Usage:
 * import config from '@/config/index.js'
 * console.log(config.apiBaseUrl) // http://127.0.0.1:8000
 */

const environment = import.meta.env.VITE_ENVIRONMENT || 'development'

const config = {
  // Current environment: 'development', 'staging', 'production'
  environment,

  // API Configuration
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : ''),
  
  // Google Maps
  googlePlacesApiKey: import.meta.env.VITE_GOOGLE_PLACES_API_KEY || '',
  
  // Encryption
  encryptionKey: import.meta.env.VITE_ENCRYPTION_KEY || '',

  // Feature Flags
  isDevelopment: environment === 'development',
  isStaging: environment === 'staging',
  isProduction: environment === 'production',

  // Environment-specific settings
  settings: {
    development: {
      logLevel: 'debug',
      apiTimeout: 30000,
      enableMockData: false,
      enableDetailedErrors: true,
    },
    staging: {
      logLevel: 'info',
      apiTimeout: 20000,
      enableMockData: false,
      enableDetailedErrors: true,
    },
    production: {
      logLevel: 'warn',
      apiTimeout: 15000,
      enableMockData: false,
      enableDetailedErrors: false,
    },
  },

  /**
   * Get setting value for current environment
   * @param {string} key - Setting key (e.g., 'logLevel')
   * @returns {*} Setting value
   */
  getSetting(key) {
    const envSettings = this.settings[this.environment]
    return envSettings?.[key] ?? this.settings.development[key]
  },

  /**
   * Get API URL for an endpoint
   * @param {string} endpoint - API endpoint path (e.g., '/api/equipments/')
   * @returns {string} Full URL
   */
  getApiUrl(endpoint) {
    const base = this.apiBaseUrl.replace(/\/$/, '')
    return `${base}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`
  },

  /**
   * Print current configuration (for debugging)
   */
  logConfig() {
    console.log('=== Frontend Configuration ===')
    console.log('Environment:', this.environment)
    console.log('API Base URL:', this.apiBaseUrl)
    console.log('Settings:', this.settings[this.environment])
    console.log('================================')
  },
}

export default config
